export const MAP_DECOR_OUTPUT_LAYOUT = "map-decor-sheet";
export const MAP_DECOR_SOURCE = {
  name: "Natural Earth 1:10m",
  url: "https://www.naturalearthdata.com/",
  version: "Natural Earth vector master · 1:10m · geografisk revidert 2026-08-08",
  rights: "Offentlig domene. Kan brukes kommersielt uten lisensavgift.",
};

export const MAP_DECOR_SIZES = [
  { value: "small", label: "Liten", description: "Fin over skrivebord, kommode eller i en lesekrok", widthMm: 1000, heightMm: 580, spreadLabel: "100 × 58 cm", panelCount: 1, panelWidthMm: 1000, priceDeltaNok: 0 },
  { value: "medium", label: "Medium", description: "Et tydelig blikkfang på barnerom eller hjemmekontor", widthMm: 1500, heightMm: 870, spreadLabel: "150 × 87 cm", panelCount: 1, panelWidthMm: 1500, priceDeltaNok: 350, popular: true },
  { value: "large", label: "Stor", description: "Fyller veggen over seng, sofa eller møtebord", widthMm: 2000, heightMm: 1160, spreadLabel: "200 × 116 cm", panelCount: 2, panelWidthMm: 1000, priceDeltaNok: 850 },
  { value: "max", label: "Maks", description: "Maksformat tilpasset vår 161 cm brede produksjon", widthMm: 2600, heightMm: 1500, spreadLabel: "260 × 150 cm", panelCount: 2, panelWidthMm: 1300, priceDeltaNok: 1550 },
];

const definitions = [
  ["world-classic", "Verdenskart · klassisk", "Verden", "Land, utvalgte hovedsteder og verdenshav"],
  ["world-soft", "Verdenskart · nordisk ro", "Verden", "Dempede farger for stue, kontor og barnerom"],
  ["world-kids", "Verdenskart · små oppdagere", "Barn", "Kontinenter, land og hav i tydelige farger"],
  ["europe-classic", "Europakart · land og hovedsteder", "Europa", "Europa med land, hovedsteder og småstater"],
  ["europe-soft", "Europakart · dempet", "Europa", "Stilrent Europa med utvalgte byer"],
  ["nordics", "Skandinavia · land og viktige byer", "Skandinavia", "Kun Norge, Sverige og Danmark, med viktige byer"],
  ["norway", "Norgeskart · byer og steder", "Norge", "Kun Norge, med viktige byer fra Kristiansand til Tromsø"],
  ["africa", "Afrikakart", "Verdensdeler", "Land, hovedsteder og tydelige kystlinjer"],
  ["asia", "Asiakart", "Verdensdeler", "Asia med land og utvalgte hovedsteder"],
  ["north-america", "Nord-Amerika", "Verdensdeler", "Canada, USA, Mexico, Mellom-Amerika og Karibia"],
  ["south-america", "Sør-Amerika", "Verdensdeler", "Land og hovedsteder fra Colombia til Ildlandet"],
  ["oceania", "Oseania og Stillehavet", "Verdensdeler", "Australia, New Zealand og øystatene i Stillehavet"],
];

export const MAP_DECOR_MAPS = definitions.map(([value, label, category, description]) => ({
  value, label, category, description,
  imageUrl: `/map-decor/${value}-preview.webp`,
  thumbnailUrl: `/map-decor/${value}-thumb.webp`,
  productionAssetUrl: `/map-decor/${value}.svg`,
  sourceName: MAP_DECOR_SOURCE.name,
  sourceUrl: MAP_DECOR_SOURCE.url,
  sourceVersion: MAP_DECOR_SOURCE.version,
  labelsLanguage: "nb-NO",
  commercialRightsConfirmed: true,
  geographyValidated: true,
  geographyValidatedAt: "2026-08-05",
  rightsNote: MAP_DECOR_SOURCE.rights,
}));

export function resolveMapDecorSize(template, designOrResponses = {}) {
  const options = template?.sizeOptions?.length ? template.sizeOptions : MAP_DECOR_SIZES;
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "mapDecorSize");
  const value = responses?.[field?.id] || field?.defaultValue || template?.defaultSizeValue || "medium";
  return options.find((item) => item.value === value) || options[0];
}

export function resolveMapDecorMap(template, designOrResponses = {}) {
  const responses = designOrResponses.responses || designOrResponses;
  const field = (template?.fields || []).find((item) => item.role === "mapDecorMap");
  const value = responses?.[field?.id] || field?.defaultValue;
  return field?.options?.find((item) => item.value === value) || field?.options?.[0] || null;
}

export function validateMapDecorTemplate(template) {
  if (template?.outputLayout !== MAP_DECOR_OUTPUT_LAYOUT) return [];
  const issues = [];
  const sizes = template.sizeOptions || [];
  const maps = (template.fields || []).find((field) => field.role === "mapDecorMap")?.options || [];
  const production = template.decorProduction || {};
  const bleedMm = Number(production.bleedMm ?? template.bleedMm);
  if (!sizes.length) issues.push(issue("sizes_missing", "Legg til minst én størrelse."));
  if (!maps.length) issues.push(issue("maps_missing", "Legg til minst ett kart."));
  if (bleedMm < 2 || bleedMm > 5) issues.push(issue("bleed_invalid", "Kartproduksjon skal ha 2-5 mm utfallende trykk."));
  if (production.cutContourName !== "CutContour") issues.push(issue("cut_contour_invalid", "Spotfargen må hete CutContour."));
  if (Number(production.rasterDpi) < 72) issues.push(issue("resolution_invalid", "Produksjonsoppløsningen må være minst 72 dpi i sluttformat."));
  for (const size of sizes) {
    if (!size.value || Number(size.widthMm) <= 0 || Number(size.heightMm) <= 0) issues.push(issue("size_invalid", `${size.label || "Størrelsen"} er ugyldig.`));
    if (Number(size.panelWidthMm || size.widthMm) + bleedMm * 2 > 1610) issues.push(issue("panel_too_wide", `${size.label} har en panelbredde inkludert bleed over printerens maksimum på 1610 mm.`));
    if (Number(size.panelCount || 1) * Number(size.panelWidthMm || size.widthMm) !== Number(size.widthMm)) issues.push(issue("panels_invalid", `${size.label} har inkonsistent paneloppsett.`));
  }
  const values = new Set();
  for (const map of maps) {
    if (!map.value || values.has(map.value)) issues.push(issue("map_id_invalid", `${map.label || "Kartet"} mangler unik ID.`));
    values.add(map.value);
    if (!map.thumbnailUrl || !map.imageUrl) issues.push(issue("map_preview_missing", `${map.label || "Kartet"} mangler forhåndsvisning.`));
    if (!map.productionAssetUrl?.toLowerCase().endsWith(".svg")) issues.push(issue("map_vector_missing", `${map.label || "Kartet"} må ha en SVG-produksjonsfil.`));
    if (!map.sourceName || !map.sourceVersion || !map.sourceUrl) issues.push(issue("map_source_missing", `${map.label || "Kartet"} mangler dokumentert kartgrunnlag.`));
    if (map.commercialRightsConfirmed !== true) issues.push(issue("map_rights_missing", `Kommersielle rettigheter må bekreftes for ${map.label || "kartet"}.`));
    if (map.geographyValidated !== true || !map.geographyValidatedAt) issues.push(issue("map_geo_missing", `${map.label || "Kartet"} mangler geografisk kvalitetskontroll.`));
  }
  return issues;
}

function issue(code, message) { return { code, message }; }
